import type { Delivery } from '../types.js';
import type { SkillApplicationV2 } from './types.js';
import { resolveWeaponContactApplication } from '../sim/weapon-contact.js';

export interface ApplicationDeliveryResolution {
  readonly ok: true;
  readonly delivery: Delivery;
}

export interface ApplicationDeliveryFailure {
  readonly ok: false;
  readonly code: string;
  readonly message: string;
  readonly path: string;
}

/**
 * Lowers the currently supported canonical application shapes to the legacy
 * delivery vocabulary without choosing a different geometry or placement.
 * Generation uses this before numeric authoring; compilation uses the same
 * function at the runtime boundary.  Unsupported spatial forms fail closed.
 */
export function resolveLegacyDeliveryForApplication(
  application: SkillApplicationV2,
): ApplicationDeliveryResolution | ApplicationDeliveryFailure {
  const { placement, geometry, motion, contactPolicy, lifecycle } = application;
  if (geometry.owner !== 'SKILL_OWNED') {
    const weapon = resolveWeaponContactApplication(application);
    if (!weapon.ok) return weapon;
    if (!['INSTANT', 'WEAPON_SWEEP', 'THRUST'].includes(motion.kind) || lifecycle.kind !== 'INSTANT') {
      return {
        ok: false,
        code: 'WEAPON_CONTACT_RUNTIME_UNIMPLEMENTED',
        message: 'weapon contact supports instant or progressive angular motion with an instant lifecycle',
        path: !['INSTANT', 'WEAPON_SWEEP', 'THRUST'].includes(motion.kind) ? 'application.motion' : 'application.lifecycle',
      };
    }
    if (placement.kind !== 'RESOLVED_ACTOR' && placement.kind !== 'RESOLVED_POINT' && placement.kind !== 'CASTER') {
      return {
        ok: false,
        code: 'WEAPON_CONTACT_PLACEMENT_UNSUPPORTED',
        message: 'weapon contact requires caster, actor, or point placement',
        path: 'application.placement',
      };
    }
    return {
      ok: true,
      delivery: {
        type: 'cone',
        rangeM: weapon.value.reachM,
        angleDeg: weapon.value.angleDeg,
      },
    };
  }

  if (motion.kind === 'WEAPON_SWEEP') return { ok: false, code: 'WEAPON_SWEEP_GEOMETRY_REQUIRED', message: 'sweep requires weapon contact', path: 'application.geometry' };
  if (motion.kind === 'THRUST') return { ok: false, code: 'THRUST_GEOMETRY_REQUIRED', message: 'a thrust requires weapon contact', path: 'application.geometry' };
  if (motion.kind === 'PROJECTILE') {
    // A projectile is lowered from its motion alone: its body is the
    // collision radius. A geometry that states another shape would be read
    // by nothing and the cast would fly as a plain arrow, so it is refused.
    if (geometry.shape.kind !== 'POINT') {
      return {
        ok: false,
        code: 'PROJECTILE_GEOMETRY_UNIMPLEMENTED',
        message: 'a projectile is shaped by its collision radius; a circle, sector or capsule geometry on it has no current runtime',
        path: 'application.geometry.shape',
      };
    }
    if (placement.kind === 'CASTER' || placement.kind === 'CONTACT_POINT' || placement.kind === 'MOVING_ATTACK') {
      return {
        ok: false,
        code: 'PROJECTILE_PLACEMENT_UNSUPPORTED',
        message: 'the current projectile runtime requires an actor/point/direction goal',
        path: 'application.placement',
      };
    }
    if (application.acquisitionRangeM !== undefined && application.acquisitionRangeM !== motion.maxTravelM) {
      return {
        ok: false,
        code: 'PROJECTILE_ACQUISITION_RANGE_MISMATCH',
        message: 'projectile acquisitionRangeM must equal maxTravelM in the current runtime',
        path: 'application.acquisitionRangeM',
      };
    }
    if (motion.sourceLifetimePolicy !== undefined && motion.sourceLifetimePolicy !== 'CONTINUE_LAST_DIRECTION') {
      return {
        ok: false,
        code: 'PROJECTILE_SOURCE_LIFETIME_UNIMPLEMENTED',
        message: 'the current projectile runtime only supports CONTINUE_LAST_DIRECTION',
        path: 'application.motion.sourceLifetimePolicy',
      };
    }
    return {
      ok: true,
      delivery: {
        type: 'projectile',
        rangeM: motion.maxTravelM,
        speedMps: motion.speedMps,
        radiusM: motion.collisionRadiusM,
        replicationMode: motion.replicationMode,
        maxPierce: contactPolicy.maxPierce,
        pierceFalloff: contactPolicy.pierceFalloff,
      },
    };
  }

  const shape = geometry.shape;
  if (shape.kind === 'CAPSULE') {
    return {
      ok: false,
      code: 'CAPSULE_RUNTIME_UNIMPLEMENTED',
      message: 'capsule geometry has no current runtime compiler',
      path: 'application.geometry.shape',
    };
  }
  if (shape.kind === 'CIRCLE' && placement.kind === 'CASTER') {
    // An instant circle centred on the caster: resolved at the caster's own
    // release point with no target acquisition and no aim error. A circle
    // that must FOLLOW the caster over time (persistent/sustained) has no
    // runtime — the area scheduler only holds fixed points — so it stays
    // refused rather than being lowered to a circle left behind at the cast
    // position.
    if (lifecycle.kind !== 'INSTANT') {
      return {
        ok: false,
        code: 'CASTER_CIRCLE_LIFECYCLE_RUNTIME_UNIMPLEMENTED',
        message: 'the current runtime cannot keep a persistent or sustained circle centred on a moving caster',
        path: 'application.lifecycle',
      };
    }
    return { ok: true, delivery: { type: 'ground_aoe', radiusM: shape.radiusM, center: 'CASTER' } };
  }
  if (shape.kind === 'POINT') {
    if (placement.kind === 'CASTER') return { ok: true, delivery: { type: 'self' } };
    if (placement.kind === 'RESOLVED_ACTOR') {
      return {
        ok: true,
        delivery: {
          type: 'targeted_enemy',
          ...(application.acquisitionRangeM === undefined ? {} : { rangeM: application.acquisitionRangeM }),
        },
      };
    }
    return {
      ok: false,
      code: 'POINT_PLACEMENT_UNSUPPORTED',
      message: 'point geometry requires caster or resolved actor placement in the current runtime',
      path: 'application.placement',
    };
  }
  if (shape.kind === 'SECTOR') {
    if (placement.kind !== 'RESOLVED_ACTOR' && placement.kind !== 'RESOLVED_POINT') {
      return {
        ok: false,
        code: 'SECTOR_PLACEMENT_UNSUPPORTED',
        message: 'sector geometry requires an actor or point goal',
        path: 'application.placement',
      };
    }
    if (lifecycle.kind !== 'INSTANT') {
      return {
        ok: false,
        code: 'PERSISTENT_SECTOR_UNSUPPORTED',
        message: 'the current runtime only persists area circles',
        path: 'application.lifecycle',
      };
    }
    return {
      ok: true,
      delivery: { type: 'cone', rangeM: shape.reachM, angleDeg: shape.angleDeg },
    };
  }

  if (placement.kind !== 'RESOLVED_ACTOR' && placement.kind !== 'RESOLVED_POINT' && placement.kind !== 'CASTER') {
    return {
      ok: false,
      code: 'CIRCLE_PLACEMENT_UNSUPPORTED',
      message: 'circle geometry requires a caster, actor, or point goal',
      path: 'application.placement',
    };
  }
  // INSTANT and SUSTAINED both lower to a single-application area: a SUSTAINED
  // skill's repetition is driven by the channel loop (`src/sim/channel.ts`),
  // NOT by the area scheduler's own `durationS`, so it must not carry one here
  // or the pulses would be double-counted. The `sustain` marker is attached by
  // the compiler (`compileCanonicalSkill`).
  if (lifecycle.kind === 'INSTANT' || lifecycle.kind === 'SUSTAINED') {
    return { ok: true, delivery: { type: 'ground_aoe', radiusM: shape.radiusM } };
  }
  return {
    ok: true,
    delivery: {
      type: 'ground_aoe',
      radiusM: shape.radiusM,
      durationS: lifecycle.durationS,
      pulseIntervalS: lifecycle.mode === 'PULSE' ? lifecycle.hitIntervalS : undefined,
      collisionCheckIntervalS: lifecycle.collisionCheckIntervalS,
      targetPolicy: lifecycle.targetPolicy,
    },
  };
}
